/**
 * TYPING IN JAVASCRIPT
 **********************
 * Karubi categorical approach
 *****************************
 * The main idea is to represent a type T with a function T
 *   that maps every object x of that type x:T to an object T(x)
 *   semantically equivalent to x and fails if x is not of the type T.
 * An assertion that an object (or an expression) x is of the type T is T(x).
 * For instance, given a function f, an expression Q(f(P(x))) is
 *   semantically equivalent to f(x) assuming that x is of the type P
 *   and f(x) is of the type Q.
 * If either object does not match its type, an exception is risen.
 * The main goal is to identify type errors as soon as possible.
 * @author Igor Slieptsov <igor.slieptsov@mail.com>
 * @version 1.0 (Dec 2016)
 * @version 1.1 (Sep 2017)
 * @version 2.0 (June 2019) - version 1.1 with EcmaScript 2015 modules
 * @module typing
 */



/* IMPORT SECTION */

import isNumber from 'lodash/isNumber';
import isBoolean from 'lodash/isBoolean';
import isString from 'lodash/isString';
import isObject from 'lodash/isObject';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import isEqual from 'lodash/isEqual';
import keys from 'lodash/keys';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';
import identity from 'lodash/identity';
import clone from 'lodash/clone';
import last from 'lodash/last';



export const config = { typechecking: true };


/**
 * A string name of unknown type used when typeName is not given.
 */
const unknownTypeName = "<…>";

/**
 * An identification function.
 * @callback Id
 * @param obj - an object to verify
 * @return an object equivalent to obj in sence of given type
 * @throws {@link module:JSTyping.IdError IdError}
 *   if an object fails the verification
 * @global
 */

/**
 * basic constructor of an identification error.
 * @param {string} msg - a message
 * @param {*} obj - an object failing type checking
 * @param {Id} [id] - an identification
 * @return an IdError object
 * @constructor
 * @static
 */
export function IdError(msg, obj, id) {
  if(keys(this).length > 0) return new IdError(msg, obj, id);
  this.msg = msg;
  this.obj = obj;
  if(id) this.id = id;
}

IdError.prototype = {
  /**
   * trace of IdError contains only one error.
   * @return an array of errors
   * @memberof module:JSTyping.IdError
   */
  showTrace: function() {
    return [this];
  },
  /**
   * @return string
   * @memberof module:JSTyping.IdError
   */
  toString: function() {
    console.error(this);
    if(this.showTrace)
      console.error(this.showTrace());
    return this.msg;
  }
}

/**
 * an error wrapped with additional information.
 * It is used in situations when one type is based on another type
 *   and an error thrown by the latter is extended with additional
 *   information by the first.
 * @param {IdError} err - an error to wrap
 * @param {string} msg - a message describing where err arises
 * @param {*} obj - an object failing type checking
 * @param {Id} [id] - an identification
 * @constructor
 * @extends module:JSTyping.IdError
 * @static
 */
export function WrappedIdError(err, msg, obj, id) {
  if(keys(this).length > 0) return new WrappedIdError(err, msg, obj, id);
  this.err = err;
  this.msg = msg;
  this.obj = obj;
  if(id) this.id = id;
}

WrappedIdError.prototype = new IdError();
/**
 * trace of WrappedIdError is `err`'s trace with this error added.
 */
WrappedIdError.prototype.showTrace = function() {
  var trace = this.err.showTrace ? this.err.showTrace() : [ this.err ];
  trace.push(this);
  return trace;
};
/**
 * Default message function.
 * @param {string|function} msg
 * @return string
 */
WrappedIdError.prototype.msg = function(msg) {
  if(isString(msg)) this.msg = msg;
  else if(isFunction(msg)) this.msg = StringId(msg(this.err, this.obj));
  throw "msg must be a string or a function";
};

/**
 * a function mapping an error object to another one.
 * @callback MapErrorCallback
 * @param {IdError|*} err
 * @return IdError
 */

/**
 * MapErrorId represents the same id as its first argument
 * but an error object is mapped with given function.
 * @param {Id} id - an identification
 * @param {module:JSTyping~MapErrorCallback} map - 
 *   a function mapping an error object to another one
 */
export function MapErrorId(id, map) {
  var nid = function(obj) {
    try{
      return id(obj);
    } catch(e) {
      throw map(e);
    }
  };
  nid.typeName = id.typeName;
  return nid;
}

/**
 * @callback ErrorWrapFunction
 * @param {IdError|*} an error to wrap
 * @return {@link module:JSTyping~WrappedIdError WrappedIdError}
 */

/**
 * wrapIdError produces a function of id error that returns WrappedIdError.
 * In fact, it is just curried version of WrappedIdError with arguments flipped.
 * @param {*} msg - message
 * @param {*} obj - an object that failed typechecking
 * @param {Id} [id] - an identification the error was generated
 * @return {module:JSTyping~ErrorWrapFunction}
 *   a wrapper function that transforms an error.
 */
export function wrapIdError(msg, obj, id) {
  return function(e) {
    return new WrappedIdError(e, msg, obj, id);
  };
}

/**
 * WrapErrorId is a composition of MapErrorId and wrapIdError.
 * It takes a `baseId` and produces a new id that wrappes an error with
 *   wrapIdError(msg, obj, defId).
 * `defId` is somehow related to what the function returns.
 * It is usually used to define a complex id in the following way:
 * ```
 * function NewId(obj) {
 *   ...
 *   WrapErrorId(PropId(obj.prop), "prop failed", obj, NewId);
 *   ...
 * }
 * ```
 * @param {Id} baseId - an identification the error is based on
 * @param {string} msg - a message to wrap with
 * @param obj - an object that failed
 * @param {Id} [defId] - another identification the error is thrown by
 * @return an identification
 */
export function WrapErrorId(baseId, msg, obj, defId) {
  return MapErrorId(baseId, wrapIdError(msg, obj, defId));
}

/**
 * check whether an object satisfies given type identification.
 * By default, an object `obj` satisfies `id` iff `id(obj)` does not fail.
 * If doublecheck argument is truthy, the additional condition
 *   `id(id(obj)) ~ id(obj)` is checked.
 * By default, JSON-structural equality is checked.
 * `eq` argument is used as equality predicate, if given.
 * Otherwise, `id.eq` is used, if given.
 * @param {*} obj - any value
 * @param {Id} id - an identification function
 * @param {boolean} [doublecheck=false] - optional boolean parameter
 * @param {function} [eq] - boolean function of two arguments
 * @return boolean
 */
function doesSatisfy(obj, id, doublecheck, eq) {
  if(!isFunction(id))
    throw "id is not an identification";
  try {
    var obj1 = id(obj);
    if(doublecheck) {
      var obj2 = id(obj1);
      if(!isFunction(eq))
        eq = isFunction(id.eq) ? id.eq : isEqual;
      if(!eq(obj1, obj2))
        throw IdError("id(id(obj)) differs from id(obj)", obj, id);
    }
  } catch(e) {
    return false;
  }
  return true;
}

/**
 * Use transparent typechecking mode.
 * By default, id can return any object on well-format argument.
 * It might be structurally equivalent object or reinterpretation.
 * The only restriction is an equality of `id(id(x))` and `id(x)`,
 *   given by `id.eq` or `isEqual`.
 * That id transformation assures to return exactly given argument.
 * For example, `y = UntypedArrayId(x = [1,2,3])` returns an array
 *   `[1, 2, 3]` with referencial difference `x != y`, while
 *   `y = TransparentId(UntypedArrayId)(x)` is equal to `y = x`
 *   (up to type checking and throwing an error in case)
 * @param id - an identification
 * @return {Id} an identification, returning its argument
 */
export function TransparentId(id) {
  var sid = function transparent(x) {
    if(!config.typechecking) return x;
    id(x);
    return x;
  };
  if(id.typeName)
    sid.typeName = id.typeName;
  Object.defineProperty(sid, 'name', {value: id.name, writable: false});
  return sid;
}

/**
 * Cached variant of id.
 * An array of object satisfying `id` is stored in `passed` field
 *   each item is `{ obj, value }`
 * @param {Id} id - an identification function
 * @return an identification function
 */
export function CacheIdentification(id) {
  var passed = [];
  // This hach is used to see a real function name in the debugger
  // not just 'cached'
  var cached = eval("(function " + id.name + "(obj) {\
    var c = find(passed, function(x) { return x.obj === obj; });\
    if(c !== undefined) return c.value;\
    var value = id(obj);\
    passed.push({ obj: obj, value: value });\
    if(passed.length > 100000) { console.log('limit reached'); passed = []; }\
    return value;\
  })");
  //Object.defineProperty(cached, 'name', {value: id.name, writable: false});
  cached.typeName = id.typeName;
  return cached;
}

/**
 * Wrapper of id that transforms it with pre-defined transformations.
 * It may involve cache and transparency.
 * @param {Id} id - an identification function
 * @return {Id} an identification function
 */
export function Identification(id) {
  return id;
  //return CacheIdentification(TransparentId(id));
}



////////////////////////   PRIMITIVES   ///////////////////////////////

/**
 * @callback Predicate
 * @param {*} obj - an object to test
 * @return boolean
 * @global
 */

/**
 * produce an identification by a predicate.
 * @param {Predicate} p - predicate (unary boolean function)
 * @param {string} msg - a message to show when the predicate returns false
 * @param {string} [typeName] - typeName property of identification
 */
export function typeIdByPredicate(p, msg, typeName) {
  var id = function(obj) {
    if(!p(obj)) throw new IdError(msg, obj, id);
    return obj;
  };
  id.typeName = typeName;
  return id;
}

export const ObjectId = typeIdByPredicate(isObject, "not an object", "Object");

export const NumberId = typeIdByPredicate(isNumber, "not a number", "Number");

export const BooleanId = typeIdByPredicate(isBoolean, "not a boolean", "Boolean");

export const StringId = typeIdByPredicate(isString, "not a string", "String");

/**
 * accept only specified value, reject others.
 * @param {*} value - any value
 * @param {string} [typeName] - typeName property of identification
 */
export function SingletonId(value, typeName) {
  var id = function(obj) {
    if(obj !== value)
      throw new IdError("an object " + obj + " is not " + value, obj, id);
    return obj;
  };
  id.typeName = typeName === undefined ? value : typeName;
  return id;
}






///////////////////////   ARRAY   //////////////////////////////

/**
 * UntypedArrayId checks whether its argument is JS Array.
 */
export let UntypedArrayId = function UntypedArrayId(a) {
  if(!isArray(a)) throw new IdError("not an array", a, UntypedArrayId);
  return a;
};
UntypedArrayId.typeName = "untyped array";
UntypedArrayId = Identification(UntypedArrayId);

/**
 * ArrayIdError is a specified id error constructor based on WrappedIdError.
 * @param {IdError} err - an error of failing element
 * @param {Id} elementType - id applied to elements
 * @param {Array} arr - an array itself
 * @param {Id} id - defining identification function
 * @constructor
 * @extends module:JSTyping.WrappedIdError
 * @static
 */
export function ArrayIdError(err, elementType, arr, id) {
  if(keys(this).length > 0)
    return new ArrayIdError(err, elementType, arr, id);
  this.msg = "an array has an element with wrong type";
  this.err = err;
  this.elementType = elementType;
  this.obj = arr;
  if(id) this.id = id;
};

ArrayIdError.prototype = new WrappedIdError();

/**
 * An identification constructor that identify an array of elements which
 * satisfy `type` identification function.
 * @param {Id} type - an identification of elements
 * @return {Id} specified identification function
 */
export let TypedArrayId = function(type) {
  if(!type) throw "elements' type is not specified";
  var id = function SpecifiedTypedArrayId(obj) {
    if(!isArray(obj)) throw new IdError("not an array", obj, id);
    try {
      return obj.map(type);
    } catch(e) {
      throw new ArrayIdError(e, type, obj, id);
    }
  };
  id.typeName = type.typeName === undefined ?
    "typed array" : "array of " + type.typeName;
  return Identification(id);
};
TypedArrayId.typeName = "typed array constructor";




////////////////////////////   OBJECT   //////////////////////////////


/**
 * FormatIdError represents an id error.
 * @param {IdError} errors - an array of errors.
 *   Each error is either a string or { err, msg }
 * @param {*} obj - an object that fails
 * @param {Object} format - a foramt object
 * @param {Id} id - an identification
 * @constructor
 * @extends module:JSTyping.IdError
 * @static
 */
export function FormatIdError(errors, obj, format, id) {
  if(keys(this).length > 0) return new FormatIdError(errors, obj, format, id);
  this.errors = errors;
  this.format = format;
  this.obj = obj;
  if(id) this.id = id;
}

FormatIdError.prototype = new IdError("An object has wrong format");
FormatIdError.prototype.showTrace = function() {
  if(this.errors.length == 0) throw "no error to show";
  var trace;
  var sel = this.errors[0]; // one selected element
  if(sel.err !== undefined)
    trace = sel.err.showTrace ? sel.err.showTrace() : [ sel ];
  else
    trace = [ this.errors[0] ];
  trace.push(this);
  return trace;
};

/**
 * OptionalId(type) is an identification that accepts `undefined` and
 *   any object accepted by `type`.
 * Also, optional types in TypeId/FormatTypeId allow objects
 *   not to have corresponding field at all.
 * @param {Id} type - an identification
 * @return {Id} an identification
 */
export function OptionalId(type) {
  var id = function SpecifiedOptionalId(obj) {
    if(obj === undefined) return;
    return type(obj);
  };
  id.typeName = type.typeName ? "optional " + type.typeName : "optional type";
  id.optional = true;
  return id;
}

/**
 * format is a dictionary (object) whose values are identifications.
 * an object of the type specified by format object is an object
 *   with the same set of fields
 * more presicely, if format consists a field { key: id },
 *  an object ought to have a field with the same key and a value that satisfies id.
 * @param {*} obj - an object to verify
 * @param {Object} format - a collection of identification functions
 * @param {Id} format.x - an identification function of `obj[x]`
 *   (for arbitrary key `x`)
 * @return an object equivalent to `obj`
 * @throws {@link module:JSTyping.FormatIdError FormatIdError}
 *   - if `obj` has not a key `k` that `format` has and
 *     `format[k]` is not an `OptinalId` identification.
 *   - if `obj[k]` fails `format[k]` for some key `k` in `format`.
 */
export function TypeId(obj, format, id) {
  var errors = [];
  obj = ObjectId(obj);
  format = ObjectId(format);
  // collecting errors
  for(var k in format) {
    if(! obj.hasOwnProperty(k) && ! format[k].optional)
      errors.push("missing property \"" + k + "\"");
    else {
      var f = format[k];
      if(!isFunction(f)) {
        console.error("bad format for " + k);
        throw "a format must be an object whose values are id functions";
      }
      try {
        f(obj[k]);
      } catch(e) {
        errors.push({
          msg: f.typeName ?
                 "a property \"" + k + "\" is not " + f.typeName :
                 "a property \"" + k + "\" has wrong type",
          err: e
        });
      }
    }
  }
  if(errors.length) {
    throw new FormatIdError(errors, obj, format, id);
  }
  // just in case
  for(var k in format)
    obj[k] = format[k](obj[k]);
  return obj;
}


/**
 * a constructor of an identification of an object type whose
 *   format is given by identification functions of its values.
 * @param {Object} format -
 *   a dictionary (object) whose values are identifications;
 *   An object of the type specified by a format object is an object
 *   with the same set of fields and, more presicely, if format consists
 *   a field `{ key: id }`, an object ought to have a field with the same key
 *   and a value satisfying `id`.
 * @param {string} [typeName]
 * @return {Id} an identification function
 */
export function FormatTypeId(format, typeName) {
  format = clone(ObjectId(format));
  var id = function SpecifiedFormatTypeId(obj) {
    return TypeId(obj, format);
  };
  id.typeName = typeName || "FormatTypeId";
  return Identification(id);
}


export function OptionalFormatTypeId(args) {
  /* { objId, key, valueId, typeName, defaultValue } */
  var objId = args.objId || identity;
  var valueId = args.valueId || identity;
  if(!args.key) return identity;
  var id = function(obj) {
    obj = args.objId(obj);
    if(!obj.hasOwnProperty(args.key)) {
      if(args.defaultValue !== undefined)
        obj[args.key] = args.defaultValue;
    } else {
      obj[args.key] = args.valueId(obj[args.key]);
    }
    return obj;
  }
  if(args.typeName)
    id.typeName = args.typeName;
  else
    id.typeName = "OptionalFormatType based on " + objId.typeName;
  return Identification(id);
}





///////////////////////////   FUNCTION   ////////////////////////////

/**
 * Functions.
 * domain and codomain identification can be specified
 * if so, a wrapper is returned
 * @param {function} f
 * @param {Id} tA - an identification of domain
 * @param {Id} tB - an identification of codomain
 * @return a function equivalent to `f`
 *   that checks its argument and result as soon as possible
 */
export function FunctionId(f, tA, tB) {
  if(!tA && !tB) {
    if(isFunction(f)) return f;
    throw IdError("not a function", f, FunctionId);
  }
  if(!tA) tA = identity;
  if(!tB) tB = identity;
  // TODO: rewrite using lodash lib
  var id = function fnc() {
    try {
      var a = tA.apply(this, arguments);
    } catch(e) {
      throw WrappedIdError(e, "function argument has wrong type",
        { func: f, arguments: arguments, error: e }, id);
    }
    var r = f.call(this, a);
    try {
      return tB.call(this, r);
    } catch(e) {
      throw WrappedIdError(e, "function result has wrong type",
        { func: f, arguments: arguments, result: r, error: e }, id);
    }
  };
  id.typeName = functionTypeName(tA.typeName, tB.typeName);
  return id; // NOTE: no TransparentId, Identification wrapper, Cache etc
}

/**
 * @param {string|undefined} a - typeName of domain
 * @param {string|undefined} b - typeName of codomain
 * @return {string} typeName of `a -> b`
 * @private
 */
export function functionTypeName(a, b) {
  var f = "function";
  if(a) {
    if(b) {
      return f + " (" + a + " -> " + b + ")";
    } else {
      return f + " of " + a;
    }
  } else {
    if(b) {
      return f + " to " + b;
    } else {
      return f;
    }
  }
}




////////////////////////   UNION   ///////////////////////////

/**
 * @param {IdError[]} errors
 * @param {*} obj
 * @param {Id} [id]
 * @constructor
 * @extends module:JSTyping.IdError
 * @static
 */
export function UnionIdError(errors, obj, id) {
  if(keys(this).length > 0) return new UnionIdError(errors, obj, id);
  this.msg = "Union failed";
  this.errors = errors;
  this.obj = obj;
  this.id = id;
}

UnionIdError.prototype = new IdError();
UnionIdError.prototype.showTrace = function() {
  var traces = this.errors.map(function(e){
    return e.showTrace ? e.showTrace() : [ e ];
  });
  // sort by length and select the longest trace
  traces = sortBy(traces, "length");
  var trace = last(traces);
  trace.push(this);
  return trace;
};

/**
 * returns pretty-format typeName based on ids' typeNames.
 * @param {Id[]} ids - an array of identifications
 * @return a string
 */
function unionTypeName(ids) {
  if(ids.length == 1) return ids.typeName || unknownTypeName;
  if(ids.length > 1) {
    var tn = "";
    for(var i = 0, n = ids.length; i < n - 2; ++i)
      tn += (ids[i].typeName || unknownTypeName) + ", ";
    tn += (ids[n - 2].typeName || unknownTypeName);
    tn += " and ";
    tn += (ids[n - 1].typeName || unknownTypeName);
    return "union of " + tn;
  }
  throw "???";
}

/**
 * identification function of
 * union of two types.
 * @param {Id} id1 - first type
 * @param {Id} id2 - second type
 * @param {string} [typeName] - a name of the resulting type
 * @param {function} [f1=identity] - custom interpretation of the first type
 * @param {function} [f2=identity] - custom interpretation of the second type
 * @return {Id} an identification function
 */
export function typeUnionId(id1, id2, typeName, f1, f2) {
  var id = function SpecifiedUnionId(obj) {
    try {
      if(!f1 && !f2) return id1(obj);
      var r = id1(obj);
      var first = true;
    } catch(e1) {
      try {
        if(!f1 && !f2) return id2(obj);
        var r = id2(obj);
        var first = false;
      } catch(e2) {
        var msg =
          (id1.typeName && id2.typeName)
            ? "an object's type is neither " + id1.typeName +
              " nor " + id2.typeName
            : "an object's type was not recognised";
        var err = new UnionIdError([e1, e2], obj, id);
        err.msg = msg;
        throw err;
      }
    }
    return first ? f1(r) : f2(r);
  };
  id.ids = [id1, id2];
  id.typeName = typeName || unionTypeName([id1, id2]);
  if(f1 || f2) return id;
  return Identification(id);
}

/**
 * Union of many types.
 * @param {Id[]} ids - an array of identifications
 * @param {string} [typeName] - a name of resulting type
 * @param {function[]} interpret - an array of functions
 *   that interpret corresponding types;
 *   by default, interpretation is an identity function
 */
export function UnionId(ids, typeName, interpret) {
  if(!interpret) interpret = {};
  var id = function SpecifiedUnionId(obj) {
    var res, index;
    var errors = [];
    for(var key in ids) {
      try {
        res = ids[key](obj);
      } catch(e) {
        errors[key] = e;
        continue;
      }
      if(!interpret[key]) return res;
      return interpret[key](res);
    }
    throw new UnionIdError(errors, obj, id);
  };
  id.ids = ids;
  id.typeName = typeName || unionTypeName(ids);
  if(interpret) return id;
  return Identification(id);
}

